import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/app/store/authStore';
import { usePushNotifications } from '@/app/hooks/usePushNotifications';

/**
 * Component that handles push notification setup when user logs in.
 *
 * Behavior:
 * 1. If user hasn't been asked for permission yet, auto-prompt on login
 * 2. If already subscribed, sync subscription to update user_id in backend
 * 3. If permission denied, do nothing (user must enable in browser settings)
 */
export function PushSubscriptionSync() {
    const user = useAuthStore((state) => state.user);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const {
        syncSubscription,
        subscribe,
        isSupported,
        isSubscribed,
        permission,
        isIOSBrowser
    } = usePushNotifications();
    const previousUserIdRef = useRef<string | null>(null);
    const hasPromptedRef = useRef(false);

    useEffect(() => {
        // Reset prompt flag on logout
        if (!isAuthenticated) {
            previousUserIdRef.current = null;
            hasPromptedRef.current = false;
            return;
        }

        // Can't do anything if push not supported or iOS browser (needs PWA)
        if (!isSupported || isIOSBrowser) {
            return;
        }

        const currentUserId = user?.id || null;

        // User just logged in or changed
        if (currentUserId && currentUserId !== previousUserIdRef.current) {
            console.log('PushSubscriptionSync: User logged in', {
                previousUserId: previousUserIdRef.current,
                currentUserId,
                permission,
                isSubscribed
            });

            previousUserIdRef.current = currentUserId;

            // Small delay to ensure auth token is properly set
            const timer = setTimeout(async () => {
                if (isSubscribed) {
                    // Already subscribed - just sync to update user_id
                    console.log('PushSubscriptionSync: Syncing existing subscription...');
                    syncSubscription();
                } else if (permission === 'default' && !hasPromptedRef.current) {
                    // Not asked yet - auto-prompt for permission
                    console.log('PushSubscriptionSync: Auto-prompting for push notification permission...');
                    try {
                        await subscribe();
                        hasPromptedRef.current = true; // Only set on success
                    } catch (err) {
                        console.error('PushSubscriptionSync: Auto-prompt failed:', err);
                        // Don't set flag - allow retry on next effect run
                    }
                } else if (permission === 'granted' && !isSubscribed) {
                    // Permission granted but not subscribed - subscribe now
                    console.log('PushSubscriptionSync: Permission granted, subscribing...');
                    try {
                        await subscribe();
                    } catch (err) {
                        console.error('PushSubscriptionSync: Subscribe failed:', err);
                    }
                }
                // If permission === 'denied', do nothing - user blocked it
            }, 1500);

            return () => clearTimeout(timer);
        }
    }, [user?.id, isAuthenticated, isSupported, isIOSBrowser, permission, isSubscribed, syncSubscription, subscribe]);

    // This component doesn't render anything
    return null;
}
